import { useEffect, useRef, useState } from "react";

const W = 1080;
const H = 1350;

function drawCard(ctx, seatNumber) {
  ctx.clearRect(0, 0, W, H);

  ctx.fillStyle = "#FDFBF6";
  ctx.fillRect(0, 0, W, H);

  ctx.save();
  ctx.font = "700 640px 'Tiro Devanagari Hindi', serif";
  ctx.fillStyle = "#F7DEE4";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("निद्रा", W + 170, -70);
  ctx.restore();

  ctx.fillStyle = "#171512";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = "italic 400 62px 'Playfair Display', serif";
  ctx.fillText("nostos", 80, 130);

  ctx.strokeStyle = "rgba(23,21,18,0.12)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(80, 175);
  ctx.lineTo(W - 80, 175);
  ctx.stroke();

  ctx.font = "700 30px 'Space Mono', monospace";
  ctx.fillStyle = "#B9822E";
  ctx.fillText("F O U N D I N G   M E M B E R", 80, 470);

  ctx.font = "700 240px 'Playfair Display', serif";
  ctx.fillStyle = "#171512";
  ctx.fillText(`#${String(seatNumber).padStart(3, "0")}`, 76, 700);

  ctx.font = "italic 400 52px 'Playfair Display', serif";
  ctx.fillStyle = "rgba(23,21,18,0.75)";
  ctx.fillText("I'm building Nostos", 80, 800);
  ctx.fillText("from day one.", 80, 862);

  const chipY = 940;
  ctx.fillStyle = "#F5C7D4";
  roundRect(ctx, 80, chipY, 420, 74, 37);
  ctx.fill();
  ctx.font = "700 24px 'Space Mono', monospace";
  ctx.fillStyle = "#171512";
  ctx.textBaseline = "middle";
  ctx.fillText("ONE OF 200 EVER ISSUED", 112, chipY + 39);
  ctx.textBaseline = "alphabetic";

  ctx.strokeStyle = "rgba(23,21,18,0.12)";
  ctx.beginPath();
  ctx.moveTo(80, H - 130);
  ctx.lineTo(W - 80, H - 130);
  ctx.stroke();

  ctx.font = "700 30px 'Space Mono', monospace";
  ctx.fillStyle = "#171512";
  ctx.fillText("nostos.in", 80, H - 78);

  ctx.font = "400 24px 'Space Mono', monospace";
  ctx.fillStyle = "rgba(23,21,18,0.5)";
  ctx.textAlign = "right";
  ctx.fillText("NIDRA · SLEEP, REDEFINED", W - 80, H - 78);
  ctx.textAlign = "left";
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default function ShareCard({ seatNumber }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        await Promise.all([
          document.fonts.load("700 240px 'Playfair Display'"),
          document.fonts.load("italic 400 52px 'Playfair Display'"),
          document.fonts.load("700 30px 'Space Mono'"),
          document.fonts.load("700 640px 'Tiro Devanagari Hindi'"),
        ]);
        await document.fonts.ready;
      } catch {
        /* fonts API not available, draw with fallbacks */
      }
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (canvas) drawCard(canvas.getContext("2d"), seatNumber);
      setReady(true);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [seatNumber]);

  const getBlob = () =>
    new Promise((resolve) => canvasRef.current.toBlob((b) => resolve(b), "image/png"));

  const download = async () => {
    setBusy(true);
    const blob = await getBlob();
    setBusy(false);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nostos-founding-${String(seatNumber).padStart(3, "0")}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const share = async () => {
    setBusy(true);
    const blob = await getBlob();
    setBusy(false);
    if (!blob) return;
    const file = new File([blob], `nostos-founding-${String(seatNumber).padStart(3, "0")}.png`, {
      type: "image/png",
    });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: "I'm a Nostos Founding Member",
          text: "I just claimed a founding seat at Nostos.",
        });
        return;
      } catch {
        /* user cancelled or share failed, fall back to download */
      }
    }
    download();
  };

  return (
    <div data-testid="share-card" className="mt-8">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#171512]/50">
        share your seat
      </p>
      <div className="mt-3 overflow-hidden rounded-2xl border-2 border-[#F3B9CD] shadow-[6px_6px_0_#FDE7EF]">
        <canvas
          ref={canvasRef}
          data-testid="share-card-canvas"
          width={W}
          height={H}
          className="block w-full"
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          data-testid="share-card-share-button"
          onClick={share}
          disabled={!ready || busy}
          className="rounded-full bg-[#171512] px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#FDFBF6] transition-colors duration-200 hover:bg-[#F5C7D4] hover:text-[#171512] disabled:cursor-not-allowed disabled:opacity-50"
        >
          share
        </button>
        <button
          type="button"
          data-testid="share-card-download-button"
          onClick={download}
          disabled={!ready || busy}
          className="rounded-full border border-[#171512]/25 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#171512] transition-colors duration-200 hover:border-[#171512] hover:bg-[#FBE9EE] disabled:cursor-not-allowed disabled:opacity-50"
        >
          download
        </button>
      </div>
    </div>
  );
}
